import { useState } from 'react'
import { addDays } from 'date-fns/addDays'
import { addMonths } from 'date-fns/addMonths'
import {
  Button,
  cn,
  Label,
  PlainA,
  RadioGroup,
  CheckboxWithLabel,
  Span,
  DatePicker,
  Select,
  Divider,
  TravelerSelection,
  type DateRange
} from '@pcln/horizon'
import AutocompleteWrapper from '@/components/AutocompleteWrapper/AutocompleteWrapper'
import { generateTimeSelectOptions } from '@/components/TimeSelect/TimeSelect'
import useBootstrapData from '@/hooks/useBootstrapData'
import { DEFAULT_VALUES, LABELS } from '@/components/Cars/HorizonForm/constants'
import { useForm } from 'react-hook-form'
import type { TravelerState } from '@/types'
import { yupResolver } from '@hookform/resolvers/yup'
import useTabGroup from '@/hooks/useTabGroup'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'
import { TRAVELER_SELECTION_COLLISION_AVOIDANCE } from '@/components/SearchForm/constants'
import { formatPackageSearchHref, initialState } from '../SearchForm/utils'
import type { PackageFormTripType, PackagesFormStateType } from '../types'
import {
  getGridContainerClass,
  horizonPackagesForm
} from './HorizonPackagesForm.variants'
import {
  STAY_FLY,
  STAY_FLY_DRIVE,
  CHECKBOX_TEXT,
  RADIO_OPTIONS
} from './constants'
import { STAY_DRIVE } from '../SearchForm/TripTypes'
import usePackagesFormSubmitValues from './usePackagesFormSubmitValues'
import {
  isInfantLimitReachedPackages,
  isInfantOnInternationalTravel,
  searchFormSchema
} from '../SearchForm/validation'
import {
  getPartStaySelectedDates,
  handleDateRangeCalendarChange,
  handleSingleDateCalendarChange,
  isString
} from './utils'

export default function HorizonPackagesForm({
  onSubmit,
  pageName
}: {
  onSubmit: (
    pageName: 'homepage' | 'landing',
    data: PackagesFormStateType
  ) => void
  pageName: 'homepage' | 'landing'
}) {
  const {
    isMobile,
    webstats: { clientGUID: cguid },
    locale: { localeCode = 'en-US' } = {}
  } = useBootstrapData()
  const {
    radioButtons,
    originInput,
    destinationInput,
    timelineInput,
    passengersInput,
    advancedSearchSlot,
    checkBoxSlot,
    partStaySlot,
    carPickupSlot
  } = horizonPackagesForm()

  const {
    clearErrors,
    control,
    formState: { errors },
    handleSubmit,
    setValue
  } = useForm<PackagesFormStateType>({
    defaultValues: initialState,
    mode: 'onSubmit',
    resolver: yupResolver(searchFormSchema),
    reValidateMode: 'onChange'
  })

  const {
    watchStartLocation,
    watchEndLocation,
    watchTravelers,
    watchTripType,
    watchRcLocation,
    watchStartDate,
    watchEndDate,
    watchHotelStartDate,
    watchHotelEndDate,
    watchPartialStay
  } = usePackagesFormSubmitValues({
    control,
    clearErrors,
    initialValues: initialState
  })

  const [packagesType, setPackagesType] = useState<PackageFormTripType>(
    initialState.tripType
  )
  const [isHotelPartialStay, setIsHotelPartialStay] = useState(false)
  const [isCarPickupElsewhere, setIsCarPickupElsewhere] = useState(false)
  const [pickupTime, setPickupTime] = useState<string>(
    DEFAULT_VALUES.PICKUP_TIME
  )
  const [dropoffTime, setDropoffTime] = useState<string>(
    DEFAULT_VALUES.DROPOFF_TIME
  )
  const { registerTabElement } = useTabGroup()
  const hasOnlyHotelAndCar = packagesType === STAY_DRIVE
  const hasHotelIncluded = packagesType.includes('STAY')
  const hasHotelAndFlightIncluded =
    packagesType === STAY_FLY || packagesType === STAY_FLY_DRIVE
  const hasHotelPartStayIncluded =
    isHotelPartialStay && hasHotelAndFlightIncluded

  const handleSubmitHelper = handleSubmit((data: PackagesFormStateType) => {
    onSubmit(pageName, data)
    logSearchFormAnalytics(cguid, pageName, 'vacations', isMobile)
  })
  const currentDate = new Date()
  const calendarMaxMonth = addMonths(currentDate, 11)

  const [maxSelectableDate, setMaxSelectableDate] = useState<Date | undefined>()
  const [maxSelectablePartStayDate, setMaxSelectablePartStayDate] = useState<
    Date | undefined
  >()

  const [defaultMonth, setDefaultMonth] = useState<Date | undefined>(
    currentDate
  )
  const [defaultPartStayMonth, setPartStayDefaultMonth] = useState<
    Date | undefined
  >()
  const [defaultPickupMonth, setDefaultPickupMonth] = useState(currentDate)
  const [defaultDropoffMonth, setDefaultDropoffMonth] = useState(currentDate)

  return (
    <div className="w-auto">
      <form
        className="w-auto"
        aria-label="horizon-packages-search-form"
        onSubmit={handleSubmitHelper}
      >
        <div className="w-auto mx-auto">
          <div
            className={getGridContainerClass(
              packagesType,
              isHotelPartialStay,
              isCarPickupElsewhere
            )}
          >
            {/* Packages Type Radio Buttons */}
            <div className={radioButtons()}>
              <RadioGroup
                className="grid grid-rows-2 sm:grid-rows-1 grid-flow-col auto-cols-min gap-x-10 sm:gap-4"
                name="packagesType"
                defaultValue={STAY_FLY}
                options={RADIO_OPTIONS}
                handleChange={(value: unknown) => {
                  if (isString(value)) {
                    setPackagesType(value)
                    setValue('tripType', value)
                  }
                }}
              />
            </div>
            {/* Start Location */}
            {!hasOnlyHotelAndCar && (
              <div className={originInput()}>
                <AutocompleteWrapper
                  searchKey="startLocation"
                  label="Where from?"
                  typeAheadEndpoint="packagesOrigin"
                  onItemSelect={item => {
                    setValue('startLocation', item)
                  }}
                  errorMessage={errors.startLocation?.message}
                  ref={(elem: HTMLInputElement) => {
                    registerTabElement(elem)
                  }}
                  initialSelectedItem={watchStartLocation ?? undefined}
                />
              </div>
            )}
            {/* End Location */}
            <div className={destinationInput()}>
              <AutocompleteWrapper
                searchKey="endLocation"
                label="Where to?"
                typeAheadEndpoint="hotels"
                onItemSelect={item => {
                  setValue('endLocation', item)
                }}
                errorMessage={errors.endLocation?.message}
                ref={(elem: HTMLInputElement) => {
                  registerTabElement(elem)
                }}
                initialSelectedItem={watchEndLocation ?? undefined}
              />
            </div>

            {/* Date Picker */}
            <div
              data-testid="date-picker-departing-returning"
              className={timelineInput()}
            >
              <DatePicker
                isMobile={isMobile}
                label="Departing - Returning"
                mode="range"
                numberOfMonths={isMobile ? 12 : 2}
                handleCalendarChange={(dates: DateRange) => {
                  handleDateRangeCalendarChange(
                    setValue,
                    'startDate',
                    'endDate',
                    setDefaultMonth,
                    dates
                  )
                  if (
                    hasHotelIncluded &&
                    dates?.from &&
                    dates?.to === undefined
                  ) {
                    setMaxSelectableDate(addDays(dates.from, 28))
                  } else {
                    setMaxSelectableDate(undefined)
                  }
                }}
                errorMessage={
                  errors.startDate?.message || errors.endDate?.message
                }
                inputRef={registerTabElement}
                disabled={{
                  before: currentDate,
                  after: maxSelectableDate
                }}
                startMonth={currentDate}
                endMonth={calendarMaxMonth}
                defaultMonth={defaultMonth}
                min={hasHotelIncluded ? 1 : 0}
              />
            </div>
            {/* Hotel Part Stay Checked - Date Picker (check-in/out)  */}
            {hasHotelPartStayIncluded && (
              <div
                data-testid="date-picker-hotel-part-stay"
                className={partStaySlot()}
              >
                <DatePicker
                  isMobile={isMobile}
                  label="Check-in - Check-out"
                  mode="range"
                  numberOfMonths={isMobile ? 12 : 2}
                  selected={getPartStaySelectedDates(
                    watchHotelStartDate,
                    watchHotelEndDate,
                    watchStartDate,
                    watchEndDate
                  )}
                  handleCalendarChange={(dates: DateRange) => {
                    handleDateRangeCalendarChange(
                      setValue,
                      'hotelStartDate',
                      'hotelEndDate',
                      setPartStayDefaultMonth,
                      dates
                    )
                    if (
                      hasHotelIncluded &&
                      dates?.from &&
                      dates?.to === undefined
                    ) {
                      setMaxSelectablePartStayDate(addDays(dates.from, 28))
                    } else {
                      setMaxSelectablePartStayDate(undefined)
                    }
                  }}
                  errorMessage={
                    errors.hotelStartDate?.message ||
                    errors.hotelEndDate?.message
                  }
                  inputRef={registerTabElement}
                  disabled={{
                    before: currentDate,
                    after: maxSelectablePartStayDate
                  }}
                  startMonth={currentDate}
                  endMonth={calendarMaxMonth}
                  defaultMonth={defaultPartStayMonth || defaultMonth}
                  min={1}
                />
              </div>
            )}
            {/* Travelers */}
            <div className={passengersInput()}>
              <TravelerSelection
                isMobile={isMobile}
                isHotel={hasOnlyHotelAndCar}
                initialValues={initialState.travelers}
                roomsEnabled={hasHotelIncluded}
                ageSelectEnabled
                maxTravelers={8}
                maxChildren={6}
                tooltipErrorMessageId="travelerSelectionError"
                onChange={values => {
                  setValue('travelers', values as TravelerState)
                }}
                errorMessage={
                  isInfantOnInternationalTravel(
                    watchStartLocation,
                    watchEndLocation,
                    watchTravelers.childrenAges,
                    watchTripType
                  ) ||
                  isInfantLimitReachedPackages(
                    watchTravelers.adults,
                    watchTravelers.childrenAges,
                    watchTripType
                  ) ||
                  errors?.travelers?.childrenAges?.message
                }
                collisionAvoidance={TRAVELER_SELECTION_COLLISION_AVOIDANCE}
                disclaimer="You may only book a package for up to 8 travelers."
              />
            </div>
            {/* Pickup Car Elsewhere */}
            {isCarPickupElsewhere && hasOnlyHotelAndCar && (
              <div className={carPickupSlot()}>
                <div className="[grid-area:city]">
                  <AutocompleteWrapper
                    searchKey="cars"
                    label="City, Airport or Address"
                    typeAheadEndpoint="cars"
                    onItemSelect={item => {
                      setValue('rcLocation', item)
                    }}
                    errorMessage={errors.rcLocation?.message}
                    ref={(elem: HTMLInputElement) => {
                      registerTabElement(elem)
                    }}
                    initialSelectedItem={watchRcLocation ?? undefined}
                  />
                </div>
                <div className="**:translate-y-0 [grid-area:pickupDate]">
                  <DatePicker
                    isMobile={isMobile}
                    label="Pickup"
                    mode="single"
                    numberOfMonths={isMobile ? 12 : 1}
                    handleCalendarChange={(date: Date) => {
                      handleSingleDateCalendarChange(
                        setValue,
                        'rcPickupDate',
                        setDefaultPickupMonth,
                        date
                      )
                    }}
                    errorMessage={errors.rcPickupDate?.message}
                    inputRef={registerTabElement}
                    disabled={{
                      before: currentDate
                    }}
                    startMonth={currentDate}
                    endMonth={calendarMaxMonth}
                    defaultMonth={defaultPickupMonth}
                  />
                </div>
                <div className="[grid-area:pickupTime]">
                  <Select
                    label={LABELS.PICKUP_TIME}
                    isInset
                    menuItems={generateTimeSelectOptions(localeCode, true)}
                    initialValue={pickupTime}
                    onChange={item => {
                      setPickupTime(item)
                      setValue('rcPickupTime', item)
                    }}
                    placeholder={LABELS.PICKUP_TIME}
                  />
                </div>
                <div className="**:translate-y-0 [grid-area:dropoffDate]">
                  <DatePicker
                    isMobile={isMobile}
                    label="Drop off"
                    mode="single"
                    numberOfMonths={isMobile ? 12 : 1}
                    handleCalendarChange={(date: Date) => {
                      handleSingleDateCalendarChange(
                        setValue,
                        'rcDropoffDate',
                        setDefaultDropoffMonth,
                        date
                      )
                    }}
                    errorMessage={errors.rcDropoffDate?.message}
                    inputRef={registerTabElement}
                    disabled={{
                      before: currentDate
                    }}
                    startMonth={currentDate}
                    endMonth={calendarMaxMonth}
                    defaultMonth={defaultDropoffMonth}
                  />
                </div>
                <div className="[grid-area:dropoffTime]">
                  <Select
                    label={LABELS.DROPOFF_TIME}
                    isInset
                    menuItems={generateTimeSelectOptions(localeCode, true)}
                    initialValue={dropoffTime}
                    onChange={item => {
                      setDropoffTime(item)
                      setValue('rcDropoffTime', item)
                    }}
                    placeholder={LABELS.DROPOFF_TIME}
                  />
                </div>
              </div>
            )}
            {/* Search Button */}
            <Button
              buttonType="submit"
              type="primary"
              size="lg"
              className={cn(
                '[grid-area:unset] sm:[grid-area:submit]',
                isMobile ? 'fixed bottom-4 right-4 left-4' : ''
              )}
            >
              Search
            </Button>
            <div className="contents [grid-area:unset] lg:[grid-area:group] lg:flex items-center gap-2">
              {/* CheckBox */}
              {hasHotelIncluded && (
                <div className={checkBoxSlot()}>
                  <CheckboxWithLabel
                    checked={
                      packagesType === STAY_DRIVE
                        ? isCarPickupElsewhere
                        : isHotelPartialStay
                    }
                    handleChange={() => {
                      if (
                        packagesType === STAY_FLY ||
                        packagesType === STAY_FLY_DRIVE
                      ) {
                        setValue('partialStay', !isHotelPartialStay)
                        setIsHotelPartialStay(!isHotelPartialStay)
                        if (!isHotelPartialStay) {
                          setValue(
                            'hotelStartDate',
                            watchHotelStartDate || watchStartDate
                          )
                          setValue(
                            'hotelEndDate',
                            watchHotelEndDate || watchEndDate
                          )
                        }
                      }
                      if (packagesType === STAY_DRIVE) {
                        setValue('pickupCarSomewhere', !isCarPickupElsewhere)
                        setIsCarPickupElsewhere(!isCarPickupElsewhere)
                      }
                    }}
                    labelNode={
                      <Span textStyle="body3">
                        {CHECKBOX_TEXT[packagesType]}
                      </Span>
                    }
                    size="16"
                    name="checkbox-hotel-part-stay"
                    value="hotel"
                  />
                </div>
              )}
              {/* Divider */}
              {hasHotelAndFlightIncluded && (
                <div className="hidden h-4 lg:flex flex-row justify-center">
                  <Divider direction="vertical" />
                </div>
              )}
              {/* Advanced Search */}
              {!hasOnlyHotelAndCar && (
                <div className={advancedSearchSlot()}>
                  <Label textStyle="body3">
                    One-way flights or multiple hotels:
                  </Label>
                  <PlainA
                    textStyle="body3"
                    className="font-bold"
                    iconRight="arrow_forward"
                    href={formatPackageSearchHref(
                      pageName,
                      watchTripType,
                      watchStartLocation,
                      watchEndLocation,
                      watchStartDate,
                      watchEndDate,
                      watchTravelers,
                      watchHotelStartDate,
                      watchHotelEndDate,
                      watchPartialStay
                    )}
                    target="_blank"
                  >
                    Advanced Search
                  </PlainA>
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}
